import {
  Document,
  Page,
  Text,
  View,
  Svg,
  Path,
  Line,
  Circle,
  Image,
  Font,
  StyleSheet,
} from "@react-pdf/renderer";

Font.register({
  family: "Roboto",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
});

const formatDate = (dateVal) => {
  if (!dateVal) return "-";
  const str = dateVal.toString();
  if (str.length === 8) {
    return `${str.slice(6, 8)}-${str.slice(4, 6)}-${str.slice(0, 4)}`;
  }
  return str;
};

const formatTime24 = (timeVal) => {
  if (!timeVal) return "-";
  const str = timeVal.toString().padStart(6, "0");
  if (str.length === 6) {
    return `${str.slice(0, 2)}:${str.slice(2, 4)}:${str.slice(4, 6)}`;
  }
  return str;
};

const experianLogo =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRr7Bmh17Ph9nkWph4hyFNSkvgj_AjQFCEczm2wGp2ZHA&s";

// Section Header Component
export const SectionHeader = ({ title }) => {
  return (
    <View style={styles.sectionHeader}>
      <Svg width="9" height="9" viewBox="0 0 16 16">
        <Path
          d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l4.992-5.99a.75.75 0 0 0-.01-1.05z"
          fill="#4caf50"
        />
      </Svg>
      <Text style={styles.sectionHeaderText}>{title}</Text>
    </View>
  );
};

const ExperianPdfDocument = ({ reportData, company }) => {
  const result = reportData?.data?.result || reportData?.result;
  const headerData = result?.CreditProfileHeader;
  const scoreData = result?.SCORE?.FCIREXScore || "-1";
  const customerData =
    result?.Current_Application?.Current_Application_Details
      ?.Current_Applicant_Details;
  const addressData =
    result?.Current_Application?.Current_Application_Details
      ?.Current_Applicant_Address_Details;
  const summaryData = result?.CAIS_Account?.CAIS_Summary;
  const capsData = result?.CAPS?.CAPS_Summary;
  const nonCapsData = result?.NonCreditCAPS?.NonCreditCAPS_Summary;
  const accountsData = result?.CAIS_Account?.CAIS_Account_DETAILS || [];

  const minScore = 300;
  const maxScore = 900;
  const clampedScore = Math.min(Math.max(Number(scoreData) || minScore, minScore), maxScore);
  const percentage = (clampedScore - minScore) / (maxScore - minScore);

  const angleDeg = -180 + percentage * 180;
  const angleRad = (angleDeg * Math.PI) / 180;
  const cx = 80;
  const cy = 70;
  const needleLength = 48;
  const needleX = cx + needleLength * Math.cos(angleRad);
  const needleY = cy + needleLength * Math.sin(angleRad);

  const creditAppInfo = [
    [
      {
        label: "Name :",
        value:
          `${customerData?.First_Name || ""} ${
            customerData?.Middle_Name1 ||
            customerData?.Middle_Name2 ||
            customerData?.Middle_Name3 ||
            ""
          } ${customerData?.Last_Name || ""}`.trim() || "",
      },
      {
        label: "Mobile Phone :",
        value: customerData?.MobilePhoneNumber || "",
      },
      {
        label: "PAN :",
        value: customerData?.IncomeTaxPan || "",
      },
      {
        label: "Email :",
        value: customerData?.EMailId || "-",
      },
      {
        label: "Employment Status :",
        value: customerData?.Current_Other_Details?.Employment_Status || "-",
      },
    ],
    [
      {
        label: "Telephone :",
        value: customerData?.Telephone_Number_Applicant_1st || "-",
      },
      {
        label: "Date of Birth :",
        value: formatDate(customerData?.Date_Of_Birth_Applicant) || "",
      },
      {
        label: "Gender :",
        value: customerData?.genderCodeDescription || "",
      },
      {
        label: "Marital Status :",
        value: customerData?.Current_Other_Details?.Marital_Status || "-",
      },
      {
        label: "Credit Cards Held :",
        value:
          customerData?.Current_Other_Details
            ?.Number_of_Major_Credit_Card_Held || "-",
      },
    ],
    [
      {
        label: "Address :",
        value: addressData?.FlatNoPlotNoHouseNo || "",
      },
      {
        label: "Address 2 :",
        value: addressData?.BldgNoSocietyName || "-",
      },
      {
        label: "Landmark :",
        value: addressData?.Landmark || "-",
      },
      {
        label: "City :",
        value: addressData?.City || "",
      },
      {
        label: "State :",
        value: addressData?.stateDescription || "",
      },
      {
        label: "Pin :",
        value: addressData?.PINCode || "",
      },
    ],
  ];

  const scoreFactors = [
    {
      s_no: 1,
      name: "Recency :",
      description: "Recent Credit Account Defaults",
    },
    {
      s_no: 2,
      name: "Leverage :",
      description: "Credit Accounts with on-time re-payment history",
    },
    {
      s_no: 3,
      name: "Coverage :",
      description: "Non-delinquent and delinquent Credit Accounts",
    },
    {
      s_no: 4,
      name: "Delinquency Status :",
      description:
        "Defaults on Credit Accounts (current & recent periodic intervals)",
    },
    {
      s_no: 5,
      name: "Credit Applications :",
      description: "Credit Account Applications over last 30 days",
    },
  ];

  const reportSummary = [
    [
      {
        label: "TOTAL CREDIT ACCOUNT :",
        value: summaryData?.Credit_Account?.CreditAccountTotal ?? 0,
      },
      {
        label: "ACTIVE ACCOUNT :",
        value: summaryData?.Credit_Account?.CreditAccountActive ?? 0,
      },
      {
        label: "CREDIT DEFAULT ACCOUNT :",
        value: summaryData?.Credit_Account?.CreditAccountDefault ?? 0,
      },
      {
        label: "CREDIT ACCOUNT CLOSED :",
        value: summaryData?.Credit_Account?.CreditAccountClosed ?? 0,
      },
      {
        label: "SUIT FILED BALANCE :",
        value: summaryData?.Credit_Account?.CADSuitFiledCurrentBalance ?? 0,
      },
    ],
    [
      {
        label: "TOTAL BALANCE :",
        value:
          summaryData?.Total_Outstanding_Balance?.Outstanding_Balance_All ?? 0,
      },
      {
        label: "SECURE BALANCE :",
        value:
          summaryData?.Total_Outstanding_Balance?.Outstanding_Balance_Secured ??
          0,
      },
      {
        label: "SECURE BALANCE IN % :",
        value: `${
          summaryData?.Total_Outstanding_Balance
            ?.Outstanding_Balance_Secured_Percentage ?? 0
        }%`,
      },
      {
        label: "UNSECURE BALANCE :",
        value:
          summaryData?.Total_Outstanding_Balance
            ?.Outstanding_Balance_UnSecured ?? 0,
      },
      {
        label: "UNSECURE BALANCE IN % :",
        value: `${
          summaryData?.Total_Outstanding_Balance
            ?.Outstanding_Balance_UnSecured_Percentage ?? 0
        }%`,
      },
    ],
    [
      { label: "CAPS LAST 7 DAYS :", value: capsData?.CAPSLast7Days ?? 0 },
      { label: "CAPS LAST 30 DAYS :", value: capsData?.CAPSLast30Days ?? 0 },
      { label: "CAPS LAST 90 DAYS :", value: capsData?.CAPSLast90Days ?? 0 },
      { label: "CAPS LAST 180 DAYS :", value: capsData?.CAPSLast180Days ?? 0 },
    ],
    [
      {
        label: "NON CREDIT CAPS LAST 7 DAYS :",
        value: nonCapsData?.NonCreditCAPSLast7Days ?? 0,
      },
      {
        label: "NON CREDIT CAPS LAST 30 DAYS :",
        value: nonCapsData?.NonCreditCAPSLast30Days ?? 0,
      },
      {
        label: "NON CREDIT CAPS LAST 90 DAYS :",
        value: nonCapsData?.NonCreditCAPSLast90Days ?? 0,
      },
      {
        label: "NON CREDIT CAPS LAST 180 DAYS :",
        value: nonCapsData?.NonCreditCAPSLast180Days ?? 0,
      },
    ],
  ];

  const creditAccInfoTabColumns = [
    {
      label: "Lender",
      width: "20%",
      key: "Subscriber_Name",
      fallback: "",
      isBold: true,
    },
    {
      label: "Account Type",
      width: "16%",
      key: "accountTypeDescription",
      fallback: "",
    },
    {
      label: "Ownership",
      width: "10%",
      key: "accountholdertypecodeDescription",
      fallback: "",
    },
    {
      label: "Date Opened",
      width: "10%",
      key: "Open_Date",
      isDate: true,
      fallback: "",
    },
    {
      label: "Account Status",
      width: "10%",
      key: "accountStatusDescription",
      fallback: "",
    },
    {
      label: "Date Reported",
      width: "10%",
      key: "Date_Reported",
      isDate: true,
      fallback: "-",
    },
    {
      label: "Sanction/ Highest Credit",
      width: "10%",
      key: "Highest_Credit_or_Original_Loan_Amount",
      fallback: 0,
      alignRight: true,
    },
    {
      label: "Current Balance",
      width: "8%",
      key: "Current_Balance",
      fallback: 0,
      alignRight: true,
    },
    {
      label: "Amount Overdue",
      width: "6%",
      key: "Amount_Past_Due",
      fallback: 0,
      alignRight: true,
    },
  ];

  const holderRecords = accountsData.map((acc) => {
    const holder = acc?.CAIS_Holder_Details?.[0] || {};
    const addressDetails = acc?.CAIS_Holder_Address_Details?.[0] || {};
    const phoneDetails = acc?.CAIS_Holder_Phone_Details?.[0] || {};

    const fullName = [
      holder.First_Name_Non_Normalized,
      holder.Middle_Name_1_Non_Normalized ||
        holder.Middle_Name_2_Non_Normalized ||
        holder.Middle_Name_3_Non_Normalized,
      holder.Surname_Non_Normalized,
    ]
      .filter(Boolean)
      .join(" ");

    const fullAddress = [
      addressDetails.First_Line_Of_Address_non_normalized,
      addressDetails.Second_Line_Of_Address_non_normalized,
      addressDetails.Third_Line_Of_Address_non_normalized,
      addressDetails.City_non_normalized,
      addressDetails.ZIP_Postal_Code_non_normalized,
    ]
      .filter(Boolean)
      .join("\n");

    return {
      name: fullName || "",
      dob: holder.Date_of_birth ? formatDate(holder.Date_of_birth) : "",
      gender: holder.genderCodeDescription || "Female",
      mobile: phoneDetails.Mobile_Telephone_Number || "",
      telephone: phoneDetails.Telephone_Number || "",
      email: phoneDetails.EMailId || "-",
      address: fullAddress || "",
      pan: holder.Income_TAX_PAN || "",
    };
  });

  const cusDetailLeftFields = [
    {
      label: "Name :",
      value:
        `${customerData?.First_Name || ""} ${
          customerData?.Middle_Name1 ||
          customerData?.Middle_Name2 ||
          customerData?.Middle_Name3 ||
          ""
        } ${customerData?.Last_Name || ""}`.trim() || "",
      isBold: true,
    },
    {
      label: "Mobile Phone :",
      value: customerData?.MobilePhoneNumber || "",
      isBold: true,
    },
    { label: "PAN :", value: customerData?.IncomeTaxPan || "" },
    { label: "Email :", value: customerData?.EMailId || "" },
    {
      label: "Employment Status :",
      value: customerData?.Current_Other_Details?.Employment_Status || "-",
    },
  ];

  const cusDetailRightFields = [
    {
      label: "Telephone :",
      value: customerData?.Telephone_Number_Applicant_1st || "-",
    },
    {
      label: "Date of Birth :",
      value: formatDate(customerData?.Date_Of_Birth_Applicant) || "",
    },
    { label: "Gender :", value: customerData?.genderCodeDescription || "" },
    {
      label: "Marital Status :",
      value: customerData?.Current_Other_Details?.Marital_Status || "-",
    },
    {
      label: "Credit Cards Held :",
      value:
        customerData?.Current_Other_Details
          ?.Number_of_Major_Credit_Card_Held || "-",
    },
  ];

  const cusDetailIdTypeDetail = [
    { type: "PAN", number: customerData?.IncomeTaxPan || "" },
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Company Branding */}
        {company && (
          <View style={styles.companyHeader}>
            {company?.logo ? (
              <Image
                src={`https://payyug.in/${company?.logo}`}
                style={styles.companyLogo}
              />
            ) : (
              <View style={styles.rowCenter}>
                <Text style={styles.fallbackLogoText}>Logo.</Text>
              </View>
            )}
            <View style={styles.companyInfoColumn}>
              <Text style={styles.companyName}>
                {company?.name}
              </Text>
              <Text style={styles.companyCin}>
                CIN Number : {company?.cin || "N/A"}
              </Text>
            </View>
          </View>
        )}

        {/* Header Branding */}
        <View style={styles.experianBrandingRow}>
          {experianLogo ? (
            <Image src={experianLogo} style={styles.experianLogo} />
          ) : (
            <View style={styles.rowCenter}>
              <Text style={styles.experianLogoFallback}>experian.</Text>
            </View>
          )}
          <Text style={styles.reportTitle}>Experian Credit Report</Text>
        </View>

        {/* Metadata */}
        <View style={styles.metadataContainer}>
          <Text style={styles.metadataText}>
            Experian Report Number ( ERN ) : {headerData?.ReportNumber || "N/A"}
          </Text>
          <Text style={styles.metadataText}>
            Report Date :{" "}
            {headerData?.ReportDate
              ? formatDate(headerData?.ReportDate)
              : "N/A"}
          </Text>
          <Text style={styles.metadataText}>
            Report Time : {formatTime24(headerData?.ReportTime) || "N/A"}
          </Text>
        </View>

        {/* 1. CREDIT APPLICATION INFORMATION */}
        <SectionHeader title="CREDIT APPLICATION INFORMATION" />
        <View style={styles.rowBetween}>
          {creditAppInfo.map((colFields, colIndex) => (
            <View key={colIndex} style={styles.appInfoColumn}>
              {colFields.map((field, fieldIndex) => (
                <View key={fieldIndex} style={styles.fieldRow}>
                  <Text style={styles.fieldLabelPrimary}>{field.label}</Text>
                  <Text style={styles.fieldValue}>{field.value}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* 2. EXPERIAN CREDIT SCORE */}
        <SectionHeader title="EXPERIAN CREDIT SCORE" />
        <View style={styles.scoreRowContainer}>
          <View style={styles.gaugeContainer}>
            <Svg width="160" height="90" viewBox="0 0 160 90">
              <Path
                d="M 20 70 A 50 50 0 0 1 34.6 35.8"
                fill="none"
                stroke="#D9534F"
                strokeWidth="18"
              />
              <Path
                d="M 37.8 32.8 A 50 50 0 0 1 64.2 21.6"
                fill="none"
                stroke="#F0AD4E"
                strokeWidth="18"
              />
              <Path
                d="M 67.8 20.2 A 50 50 0 0 1 92.2 20.2"
                fill="none"
                stroke="#FFD700"
                strokeWidth="18"
              />
              <Path
                d="M 95.8 21.6 A 50 50 0 0 1 122.2 32.8"
                fill="none"
                stroke="#5CB85C"
                strokeWidth="18"
              />
              <Path
                d="M 125.4 35.8 A 50 50 0 0 1 140 70"
                fill="none"
                stroke="#EEEEEE"
                strokeWidth="18"
              />
              <Line
                x1={cx}
                y1={cy}
                x2={needleX}
                y2={needleY}
                stroke="#2C2C2C"
                strokeWidth="3.5"
                strokeLinecap="round"
              />
              <Circle cx={cx} cy={cy} r="6" fill="#2C2C2C" />
            </Svg>
            <Text style={styles.scoreText}>{scoreData}</Text>
            <Text style={styles.scoreSubtext}>Credit Score</Text>
          </View>

          <View style={styles.scoreFactorsContainer}>
            <Text style={styles.scoreFactorsTitle}>Score Factors</Text>
            {scoreFactors.map((item) => (
              <View style={styles.scoreFactorRow} key={item.s_no}>
                <Text style={styles.factorNo}>{item.s_no}.</Text>
                <Text style={styles.factorName}>{item.name}</Text>
                <Text style={styles.factorDesc}>{item.description}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* 3. REPORT SUMMARY */}
        <SectionHeader title="REPORT SUMMARY" />
        <View style={styles.rowBetween}>
          {reportSummary.map((colFields, colIndex) => (
            <View
              key={colIndex}
              style={[
                styles.reportSummaryCol,
                colIndex !== reportSummary.length - 1 && styles.borderRight,
              ]}
            >
              {colFields.map((field, fieldIndex) => (
                <View key={fieldIndex} style={styles.fieldRow}>
                  <Text style={styles.summaryLabel}>{field.label}</Text>
                  <Text style={styles.summaryValue}>{field.value}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* 4. SUMMARY: CREDIT ACCOUNT INFORMATION */}
        <SectionHeader title="SUMMARY: CREDIT ACCOUNT INFORMATION" />
        <View style={styles.tableContainer}>
          <View style={styles.tableHeaderRow}>
            {creditAccInfoTabColumns.map((col, index) => (
              <Text
                key={index}
                style={[
                  styles.tableHeaderCell,
                  { width: col.width },
                  col.alignRight ? styles.textRight : styles.textLeft,
                ]}
              >
                {col.label}
              </Text>
            ))}
          </View>

          {accountsData.map((acc, rowIndex) => (
            <View key={rowIndex} style={styles.tableRow}>
              {creditAccInfoTabColumns.map((col, colIndex) => {
                let value = acc[col.key] ?? col.fallback;
                if (col.isDate && typeof value === "number") {
                  value = formatDate(value);
                }
                return (
                  <Text
                    key={colIndex}
                    style={[
                      styles.tableBodyCell,
                      { width: col.width },
                      col.isBold && styles.fontBold,
                      col.alignRight ? styles.textRight : styles.textLeft,
                    ]}
                  >
                    {value}
                  </Text>
                );
              })}
            </View>
          ))}
        </View>

        {/* 5. CREDIT ACCOUNT INFORMATION DETAILS */}
        <SectionHeader title="CREDIT ACCOUNT INFORMATION DETAILS" />
        <View style={styles.tableContainer}>
          {holderRecords.map((item, index) => (
            <View key={index} style={styles.holderRecordRow}>
              <View style={styles.holderCol1}>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Name :</Text>
                  <Text style={styles.holderValueBold}>{item.name}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Date of Birth :</Text>
                  <Text style={styles.holderValueBold}>{item.dob}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Gender :</Text>
                  <Text style={styles.holderValueBold}>{item.gender}</Text>
                </View>
              </View>

              <View style={styles.holderCol2}>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Mobile :</Text>
                  <Text style={styles.holderValueBold}>{item.mobile}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Telephone :</Text>
                  <Text style={styles.holderValueNormal}>{item.telephone}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Email :</Text>
                  <Text style={styles.holderValueNormal}>{item.email}</Text>
                </View>
              </View>

              <View style={styles.holderCol3}>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Address :</Text>
                  <Text style={styles.holderAddressValue}>{item.address}</Text>
                </View>
              </View>

              <View style={styles.holderCol4}>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>PAN :</Text>
                  <Text style={styles.holderValueBold}>{item.pan}</Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.holderLabel}>Email :</Text>
                  <Text style={styles.holderValueNormal}>{item.email}</Text>
                </View>
              </View>
            </View>
          ))}
        </View>

        {/* 6. ACCOUNT HISTORY */}
        <SectionHeader title="ACCOUNT HISTORY" />
        {accountsData.map((acc, index) => {
          const historyList = acc?.CAIS_Account_History || [];
          return (
            <View key={index} style={styles.accountHistoryWrapper}>
              <View style={styles.historyColLeft}>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Subscriber</Text>
                  <Text style={styles.historyValue}>
                    {acc?.Subscriber_Name || "-"}
                  </Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Account Number</Text>
                  <Text style={styles.historyValue}>
                    {acc?.Account_Number || "-"}
                  </Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Account Type</Text>
                  <Text style={styles.historyValue}>
                    {acc?.accountTypeDescription || "-"}
                  </Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Status</Text>
                  <Text style={styles.historyValue}>
                    {acc?.accountStatusDescription || "ACTIVE"}
                  </Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Credit Limit</Text>
                  <Text style={styles.historyValuePlain}>
                    {acc?.Credit_Limit_Amount ?? 0}
                  </Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Current Balance</Text>
                  <Text style={styles.historyValuePlain}>
                    {acc?.Current_Balance ?? 0}
                  </Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Date Reported</Text>
                  <Text style={styles.historyValuePlain}>
                    {formatDate(acc?.Date_Reported) || "-"}
                  </Text>
                </View>
              </View>

              <View style={styles.historyColMiddle}>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Payment Rating</Text>
                  <Text style={styles.historyValueRating}>
                    {acc?.Payment_Rating ?? 0}
                  </Text>
                </View>
                <View style={styles.historyProfileBlock}>
                  <Text style={styles.historyBlockTitle}>
                    Payment History Profile
                  </Text>
                  <Text style={styles.historyProfileText}>
                    {acc?.Payment_History_Profile || "-"}
                  </Text>
                </View>
                <View style={styles.historyProfileBlock}>
                  <Text style={styles.historyBlockTitle}>
                    Profile Description
                  </Text>
                  <Text style={styles.historyProfileDesc}>
                    {acc?.paymentHistoryProfileDescription || ""}
                  </Text>
                </View>
                <View style={styles.fieldRow}>
                  <Text style={styles.historyLabel}>Past Due</Text>
                  <Text style={styles.historyValueRating}>
                    {acc?.Amount_Past_Due ?? 0}
                  </Text>
                </View>
              </View>

              <View style={styles.historyColRight}>
                <View style={styles.historySubheaderRow}>
                  <Text style={styles.historySubheaderCell}>Year</Text>
                  <Text style={styles.historySubheaderCell}>Month</Text>
                  <Text style={styles.historySubheaderCell}>DPD</Text>
                  <Text style={styles.historySubheaderCell}>Asset</Text>
                  <Text style={styles.historySubheaderCellDesc}>
                    Asset Description
                  </Text>
                </View>
                {historyList.map((hist, hIdx) => (
                  <View key={hIdx} style={styles.historyDataRow}>
                    <Text style={styles.historyDataCell}>
                      {hist?.Year || "-"}
                    </Text>
                    <Text style={styles.historyDataCell}>
                      {hist?.Month || "-"}
                    </Text>
                    <Text style={styles.historyDataCell}>
                      {hist?.Days_Past_Due ?? 0}
                    </Text>
                    <Text style={styles.historyDataCell}>
                      {hist?.Asset_Classification || "?"}
                    </Text>
                    <Text style={styles.historyDataCellDesc}>
                      {hist?.assetClassificationDescription || ""}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          );
        })}

        {/* 7. CUSTOMER PERSONAL DETAILS */}
        <SectionHeader title="CUSTOMER PERSONAL DETAILS" />
        <View style={styles.personalDetailsWrapper}>
          <View style={styles.personalDetailsCol1}>
            {cusDetailLeftFields.map((field, index) => (
              <View key={index} style={styles.fieldRow}>
                <Text style={styles.personalLabel}>{field.label}</Text>
                <Text
                  style={[
                    styles.personalValue,
                    field.isBold && styles.personalValueBold,
                  ]}
                >
                  {field.value}
                </Text>
              </View>
            ))}
          </View>

          <View style={styles.personalDetailsCol2}>
            {cusDetailRightFields.map((field, index) => (
              <View key={index} style={styles.fieldRow}>
                <Text style={styles.personalLabel}>{field.label}</Text>
                <Text style={styles.personalValue}>{field.value}</Text>
              </View>
            ))}
          </View>

          <View style={styles.personalDetailsCol3}>
            <View style={styles.idHeaderRow}>
              <Text style={styles.idTypeHeader}>Id Type</Text>
              <Text style={styles.idNumberHeader}>Id Number</Text>
            </View>
            {cusDetailIdTypeDetail.map((item, index) => (
              <View key={index} style={styles.row}>
                <Text style={styles.idTypeValue}>{item.type}</Text>
                <Text style={styles.idNumberValue}>{item.number}</Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ExperianPdfDocument;

// Native StyleSheet for @react-pdf/renderer
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 7.5,
    fontFamily: "Helvetica",
    color: "#1f2937",
    backgroundColor: "#ffffff",
  },
  row: {
    flexDirection: "row",
  },
  rowCenter: {
    flexDirection: "row",
    alignItems: "center",
  },
  rowBetween: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  fieldRow: {
    flexDirection: "row",
    marginBottom: 6,
    gap: 4,
  },
  textRight: {
    textAlign: "right",
  },
  textLeft: {
    textAlign: "left",
  },
  fontBold: {
    fontWeight: "bold",
  },
  borderRight: {
    borderRightWidth: 1,
    borderRightColor: "#d1d5db",
  },

  // Section Header
  sectionHeader: {
    backgroundColor: "#f2f2f2",
    padding: 4,
    borderWidth: 0.5,
    borderColor: "#d1d5db",
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 6,
  },
  sectionHeaderText: {
    fontSize: 8,
    fontWeight: "bold",
    color: "#1a237e",
    textTransform: "uppercase",
    marginLeft: 4,
  },

  // Company Header
  companyHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 8,
  },
  companyLogo: {
    width: 70,
    height: 70,
    objectFit: "contain",
  },
  fallbackLogoText: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2b579a",
  },
  companyInfoColumn: {
    flexDirection: "column",
    alignItems: "flex-end",
  },
  companyName: {
    fontSize: 13,
    color: "#2b579a",
    marginBottom: 6,
    fontWeight: "bold",
  },
  companyCin: {
    fontSize: 7,
    color: "#2b579a",
    marginBottom: 2,
  },

  // Experian Branding
  experianBrandingRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    marginTop: 8,
  },
  experianLogo: {
    width: 120,
    height: 30,
    objectFit: "contain",
  },
  experianLogoFallback: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2b579a",
  },
  reportTitle: {
    fontSize: 13,
    color: "#2b579a",
    fontWeight: "bold",
  },

  // Metadata
  metadataContainer: {
    alignItems: "flex-end",
    marginBottom: 8,
  },
  metadataText: {
    fontSize: 7,
    color: "#2b579a",
    marginBottom: 2,
  },

  // 1. Credit App Info
  appInfoColumn: {
    width: "32%",
  },
  fieldLabelPrimary: {
    width: "45%",
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7,
  },
  fieldValue: {
    width: "55%",
    color: "#000000",
    fontSize: 7,
  },

  // 2. Score
  scoreRowContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginVertical: 6,
  },
  gaugeContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 4,
  },
  scoreText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1f2937",
    textAlign: "center",
    marginTop: 2,
  },
  scoreSubtext: {
    fontSize: 9,
    color: "#0070c0",
    fontWeight: "bold",
    textAlign: "center",
  },
  scoreFactorsContainer: {
    width: "50%",
    paddingLeft: 8,
  },
  scoreFactorsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 4,
    color: "#1f2937",
  },
  scoreFactorRow: {
    flexDirection: "row",
    marginBottom: 8,
  },
  factorNo: {
    width: "5%",
    fontSize: 8,
    color: "#6b7280",
  },
  factorName: {
    width: "25%",
    fontSize: 8,
    color: "#1f2937",
    fontWeight: "bold",
  },
  factorDesc: {
    width: "70%",
    fontSize: 8,
    color: "#6b7280",
  },

  // 3. Report Summary
  reportSummaryCol: {
    width: "24%",
    paddingRight: 4,
  },
  summaryLabel: {
    width: "65%",
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  summaryValue: {
    width: "35%",
    color: "#000000",
    fontSize: 7.5,
  },

  // 4. Tables
  tableContainer: {
    marginTop: 4,
    marginBottom: 8,
  },
  tableHeaderRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#2b579a",
    paddingBottom: 4,
    marginBottom: 4,
    gap: 4,
  },
  tableHeaderCell: {
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 3,
    gap: 4,
  },
  tableBodyCell: {
    color: "#1f2937",
    fontSize: 7.5,
  },

  // 5. Holder Records
  holderRecordRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingBottom: 4,
    gap: 8,
  },
  holderCol1: { width: "26%" },
  holderCol2: { width: "22%" },
  holderCol3: { width: "34%" },
  holderCol4: { width: "18%" },
  holderLabel: {
    width: "40%",
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  holderValueBold: {
    width: "60%",
    color: "#000000",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  holderValueNormal: {
    width: "60%",
    color: "#1f2937",
    fontSize: 7.5,
  },
  holderAddressValue: {
    width: "60%",
    color: "#000000",
    fontWeight: "bold",
    fontSize: 7.5,
    textTransform: "uppercase",
  },

  // 6. History Details
  accountHistoryWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
    paddingBottom: 4,
  },
  historyColLeft: { width: "32%" },
  historyColMiddle: { width: "30%", paddingLeft: 4 },
  historyColRight: { width: "35%" },
  historyLabel: {
    width: "45%",
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  historyValue: {
    width: "55%",
    color: "#1f2937",
    fontSize: 7.5,
    textTransform: "uppercase",
  },
  historyValuePlain: {
    width: "55%",
    color: "#1f2937",
    fontSize: 7.5,
  },
  historyValueRating: {
    color: "#1f2937",
    fontSize: 7.5,
    marginLeft: 6,
  },
  historyProfileBlock: {
    marginBottom: 6,
  },
  historyBlockTitle: {
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
    marginBottom: 2,
  },
  historyProfileText: {
    fontSize: 7,
    color: "#1f2937",
    letterSpacing: 0.5,
  },
  historyProfileDesc: {
    fontSize: 7,
    color: "#1f2937",
  },
  historySubheaderRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  historySubheaderCell: {
    width: "18%",
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  historySubheaderCellDesc: {
    width: "28%",
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  historyDataRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  historyDataCell: {
    width: "18%",
    color: "#1f2937",
    fontSize: 7.5,
  },
  historyDataCellDesc: {
    width: "28%",
    color: "#1f2937",
    fontSize: 7.5,
  },

  // 7. Customer Personal Details
  personalDetailsWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 2,
  },
  personalDetailsCol1: { width: "35%" },
  personalDetailsCol2: { width: "35%" },
  personalDetailsCol3: { width: "30%" },
  personalLabel: {
    width: "45%",
    color: "#2b579a",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  personalValue: {
    width: "55%",
    fontSize: 7.5,
    color: "#1f2937",
  },
  personalValueBold: {
    fontWeight: "bold",
    color: "#000000",
  },
  idHeaderRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  idTypeHeader: {
    width: "40%",
    color: "#0070c0",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  idNumberHeader: {
    width: "60%",
    color: "#0070c0",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  idTypeValue: {
    width: "40%",
    color: "#000000",
    fontWeight: "bold",
    fontSize: 7.5,
  },
  idNumberValue: {
    width: "60%",
    color: "#000000",
    fontWeight: "bold",
    fontSize: 7.5,
  },
});